import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { getIncomeSource, isWeb } from '@/utils/vip';
import { getSkuType, handleTheme } from '@/utils';
import { PeriodTabType, TabType } from '@/constants/subscription';
import UserinfoModule from './components/userinfo';
import DurationTabs, { DurationTabItem } from './components/duration-tabs';
import SubProducts from './components/sub-products';
import MeidouProducts from './components/meidou-products';
import ExchangeModule from './components/exchange';
import PayResult from '@/page/subscription/new/components/pay-result';
import BindPhone from '@/page/subscription/new/components/bind-phone';
import MeidouCountInfoModal from '@/page/subscription/new/components/meidou-count-info';
import { track } from '@/utils/common';
import { handleJumpLogin } from '@/utils/user';
import { message } from 'antd';
import { useAtom } from 'jotai';
import {
  activeIndexAtom,
  curPeriodTabAtom,
  curProductAtom,
  curProProductAtom,
  curSubProductAtom,
  curTabAtom,
  loadingAtom,
  getProductsStructureKey,
  productsAtom,
  proAllProductsAtom,
  subAllProductsAtom,
  userInfoAtom,
  visibleBindPhoneAtom,
  visibleExchangeAtom,
  visibleInvoicingAtom,
  visibleResultAtom,
  giftLevelAtom,
  hasMembershipProductInPeriod,
} from '@/store';
import {
  clearPollTimer,
  getProducts,
  initActiveProductIndex,
  initFn,
  refreshProProductsForNewUI,
} from '@/utils/subscribe';

import styles from './index.module.css';
import InvoicingModal from '../new/components/invoicing';
import { syncSubEntranceBizCode } from '@/utils/subscriptionEntrance';
import { getLocaleStore } from '@/i18n/localeStore';

const { $t: $tStatic } = getLocaleStore();

/** 时长 Tab 配置 */
const DURATION_TABS: DurationTabItem<PeriodTabType>[] = [
  { label: $tStatic((v) => v.Tab_RenewalYear), value: PeriodTabType.YEARLY },
  { label: $tStatic((v) => v.Tab_RenewalMonth), value: PeriodTabType.MONTHLY },
  { label: $tStatic((v) => v.Tab_SingleYear), value: PeriodTabType.SINGLE_YEARLY },
  { label: $tStatic((v) => v.Tab_SingleMonth), value: PeriodTabType.SINGLE_MONTHLY },
];

const Vip = () => {
  const [, setActiveIndex] = useAtom(activeIndexAtom);
  const [products, setProducts] = useAtom(productsAtom);
  const [curProduct] = useAtom(curProductAtom);
  const [curTab, setCurTab] = useAtom(curTabAtom);
  const [visibleResult] = useAtom(visibleResultAtom);
  const [visibleBindPhone] = useAtom(visibleBindPhoneAtom);
  const [userInfo] = useAtom(userInfoAtom);
  const [, setVisibleExchange] = useAtom(visibleExchangeAtom);
  const [, setVisibleInvoicing] = useAtom(visibleInvoicingAtom);
  const [curPeriodTab, setCurPeriodTab] = useAtom(curPeriodTabAtom);
  const [giftLevel] = useAtom(giftLevelAtom);
  const [subAllProducts] = useAtom(subAllProductsAtom);
  const [proAllProducts] = useAtom(proAllProductsAtom);
  const [loading] = useAtom(loadingAtom);
  const [curSubProduct] = useAtom(curSubProductAtom);
  const [curProProduct] = useAtom(curProProductAtom);
  const [hasInited, setHasInited] = useState<boolean>(false);
  const prevGiftLevelForProFetchRef = useRef<number | undefined>(undefined);

  /** 标准+高级在该周期均无商品时不展示该时长 Tab */
  const visibleDurationTabs = useMemo(
    () =>
      DURATION_TABS.filter((t) =>
        hasMembershipProductInPeriod(subAllProducts, proAllProducts, t.value)
      ),
    [subAllProducts, proAllProducts]
  );

  /** 与 SubProducts 一致：首屏拉品阶段展示骨架；支付轮询 loading 不展示 */
  const durationTabsSkeleton = loading && !curSubProduct && !curProProduct;
  const productsStructureKey = useMemo(() => getProductsStructureKey(products), [products]);

  useEffect(() => {
    if (curTab !== TabType.SUB || visibleDurationTabs.length === 0) {
      return;
    }
    const allowed = new Set(visibleDurationTabs.map((t) => t.value));
    if (!allowed.has(curPeriodTab)) {
      setCurPeriodTab(visibleDurationTabs[0].value);
    }
  }, [curTab, curPeriodTab, visibleDurationTabs, setCurPeriodTab]);

  useEffect(() => {
    handleTheme();
    initFn(() => {
      setHasInited(true);
    }, true);

    return () => {
      clearPollTimer();
    };
  }, []);

  useEffect(() => {
    if (products.length) {
      initActiveProductIndex();
    }
  }, [productsStructureKey]);

  useEffect(() => {
    // if (curProduct) {
    //   handleRefreshQrCode();
    // }
  }, [curProduct]);

  useEffect(() => {
    // 切换主 Tab 时停止上一场景的订单轮询，避免跨 Tab 仍轮询旧单
    clearPollTimer();

    if (hasInited) {
      // 同步更新入口标识
      syncSubEntranceBizCode();
      setProducts([]);
      setActiveIndex(-1);
      getProducts(true);
    }

    if (getIncomeSource() === 'MTXX-WEB') {
      track({
        event_id: 'vip_page_enter',
        params: {},
      });
    }
  }, [curTab]);

  useEffect(() => {
    if (!hasInited || curTab !== TabType.SUB) {
      return;
    }
    if (prevGiftLevelForProFetchRef.current === undefined) {
      prevGiftLevelForProFetchRef.current = giftLevel;
      return;
    }
    if (prevGiftLevelForProFetchRef.current === giftLevel) {
      return;
    }
    prevGiftLevelForProFetchRef.current = giftLevel;
    refreshProProductsForNewUI();
  }, [giftLevel, hasInited, curTab]);

  const handleDurationChange = useCallback((value: PeriodTabType) => {
    setCurPeriodTab(value);

    let classValue = '';
    switch (value) {
      case PeriodTabType.YEARLY:
        classValue = 'yearly_renew';
        break;
      case PeriodTabType.MONTHLY:
        classValue = 'monthly_renew';
        break;
      case PeriodTabType.SINGLE_YEARLY:
        classValue = 'yearly_once';
        break;
      case PeriodTabType.SINGLE_MONTHLY:
        classValue = 'monthly_once';
        break;
      default:
        break;
    }

    track({
      event_id: 'vip_page_click',
      event_name: '会员落地页点击',
      params: { class: classValue },
    });
  }, []);

  return (
    <div
      className={styles.main}
      style={{
        borderRadius: getIncomeSource() !== 'MTXX-WEB' ? 0 : '',
        width: getIncomeSource() !== 'MTXX-WEB' ? '100%' : '',
        height: getIncomeSource() !== 'MTXX-WEB' ? '100%' : '',
      }}
    >
      {/* 顶部: 用户信息 + 时长Tab */}
      <div className={styles.header}>
        <UserinfoModule />
        {curTab === TabType.SUB && (durationTabsSkeleton || visibleDurationTabs.length > 0) ? (
          <div className={styles.durationTabsContainer}>
            <DurationTabs
              tabs={durationTabsSkeleton ? DURATION_TABS : visibleDurationTabs}
              activeValue={curPeriodTab}
              onChange={handleDurationChange}
              skeleton={durationTabsSkeleton}
            />
          </div>
        ) : null}
      </div>

      <div className={styles.scrollArea}>
        {/* 卡片区域 */}
        {curTab === TabType.MEIDOU ? <MeidouProducts /> : <SubProducts />}
      </div>

      {/* 弹窗类组件 */}
      <ExchangeModule onRefresh={() => getProducts(true)} />

      {/* 支付成功弹窗 */}
      {visibleResult ? <PayResult /> : null}

      {/* 绑定手机号弹窗 */}
      {visibleBindPhone ? <BindPhone /> : null}

      {/* 美豆余额弹窗 */}
      <MeidouCountInfoModal />

      {/* 开票弹窗 */}
      <InvoicingModal />
    </div>
  );
};

export default Vip;
